/**
 * Cart Operation Interceptor
 *
 * Hooks into all Shopify cart API calls and emits unified events when operations complete.
 * This enables event-driven discount application instead of time-based debouncing.
 *
 * Events Emitted:
 * - cart:operation:start - Cart API call initiated
 * - cart:operation:complete - Cart API response received with data
 * - cart:operation:error - Cart API call failed
 */

class CartOperationInterceptor {
  constructor() {
    this.operationInProgress = false;
    this.operationQueue = [];
    this.debug = true; // Set to false in production

    this.log('Initializing Cart Operation Interceptor');
    this.setupInterceptors();
  }

  log(...args) {
    // Disabled to reduce console noise
    // if (this.debug) {
    //   console.log('[CartInterceptor]', ...args);
    // }
  }

  /**
   * Set up interceptors for all cart operation methods
   */
  setupInterceptors() {
    this.interceptFetch();
    this.interceptJQueryAjax();
    this.interceptXMLHttpRequest();
  }

  /**
   * Intercept window.fetch() for cart operations
   */
  interceptFetch() {
    const self = this;
    const originalFetch = window.fetch;

    window.fetch = function(url, options = {}) {
      // Check if this is a cart operation
      if (self.isCartOperation(url)) {
        const method = options.method || 'GET';
        const isWrite = self.isCartWriteOperation(url, method);

        // READ operations - log but don't emit completion events (prevents infinite loop)
        if (!isWrite) {
          self.log('Fetch cart READ operation (no event):', url);
          return originalFetch.apply(this, arguments);
        }

        // WRITE operations - emit events
        self.log('Fetch cart WRITE operation detected:', url);
        self.operationInProgress = true;
        self.emitEvent('cart:operation:start', {
          url,
          method,
          type: 'fetch'
        });

        return originalFetch.apply(this, arguments)
          .then(response => {
            // Clone response to read it without consuming the original
            const responseClone = response.clone();

            // Try to parse as JSON
            responseClone.json()
              .then(data => {
                self.operationInProgress = false;
                self.log('Fetch cart WRITE operation complete:', url, data);
                self.emitEvent('cart:operation:complete', {
                  url,
                  data,
                  type: 'fetch'
                });
              })
              .catch(err => {
                // Response wasn't JSON, still mark as complete
                self.operationInProgress = false;
                self.log('Fetch cart WRITE operation complete (non-JSON):', url);
                self.emitEvent('cart:operation:complete', {
                  url,
                  data: null,
                  type: 'fetch'
                });
              });

            return response;
          })
          .catch(error => {
            self.operationInProgress = false;
            self.log('Fetch cart operation error:', url, error);
            self.emitEvent('cart:operation:error', {
              url,
              error,
              type: 'fetch'
            });
            throw error;
          });
      }

      // Not a cart operation, pass through
      return originalFetch.apply(this, arguments);
    };

    this.log('Fetch interceptor installed');
  }

  /**
   * Intercept jQuery.ajax() for cart operations (legacy support)
   */
  interceptJQueryAjax() {
    if (!window.jQuery) {
      this.log('jQuery not found, skipping ajax interceptor');
      return;
    }

    const self = this;
    const originalAjax = jQuery.ajax;

    jQuery.ajax = function(url, settings) {
      // Handle both jQuery.ajax(url, settings) and jQuery.ajax(settings)
      if (typeof url === 'object') {
        settings = url;
        url = settings.url;
      }
      settings = settings || {};

      // Check if this is a cart operation
      if (self.isCartOperation(url)) {
        const method = settings.type || 'GET';
        const isWrite = self.isCartWriteOperation(url, method);

        // READ operations - don't emit events
        if (!isWrite) {
          self.log('jQuery ajax cart READ operation (no event):', url);
          return originalAjax.apply(this, arguments);
        }

        // WRITE operations - emit events
        self.log('jQuery ajax cart WRITE operation detected:', url);

        const originalSuccess = settings.success;
        const originalError = settings.error;
        const originalComplete = settings.complete;

        // Wrap success callback
        settings.success = function(data, textStatus, jqXHR) {
          self.log('jQuery ajax cart WRITE operation success:', url, data);
          self.emitEvent('cart:operation:complete', {
            url,
            data,
            type: 'jquery'
          });
          if (originalSuccess) {
            originalSuccess.apply(this, arguments);
          }
        };

        // Wrap error callback
        settings.error = function(jqXHR, textStatus, errorThrown) {
          self.log('jQuery ajax cart operation error:', url, errorThrown);
          self.emitEvent('cart:operation:error', {
            url,
            error: errorThrown,
            type: 'jquery'
          });
          if (originalError) {
            originalError.apply(this, arguments);
          }
        };

        // Wrap complete callback
        settings.complete = function(jqXHR, textStatus) {
          self.operationInProgress = false;
          if (originalComplete) {
            originalComplete.apply(this, arguments);
          }
        };

        self.operationInProgress = true;
        self.emitEvent('cart:operation:start', {
          url,
          method,
          type: 'jquery'
        });
      }

      return originalAjax.apply(this, arguments);
    };

    this.log('jQuery ajax interceptor installed');
  }

  /**
   * Intercept XMLHttpRequest for cart operations
   */
  interceptXMLHttpRequest() {
    const self = this;
    const OriginalXHR = window.XMLHttpRequest;

    window.XMLHttpRequest = function() {
      const xhr = new OriginalXHR();
      const originalOpen = xhr.open;
      const originalSend = xhr.send;
      let requestUrl = '';
      let requestMethod = '';

      // Intercept open to capture URL and method
      xhr.open = function(method, url, async, user, password) {
        requestUrl = url;
        requestMethod = method;

        // Check if this is a cart operation
        if (self.isCartOperation(url)) {
          const isWrite = self.isCartWriteOperation(url, method);

          // READ operations - don't emit events
          if (!isWrite) {
            self.log('XHR cart READ operation (no event):', url);
            return originalOpen.apply(this, arguments);
          }

          // WRITE operations - emit events
          self.log('XHR cart WRITE operation detected:', url);

          // Add event listeners
          xhr.addEventListener('loadstart', function() {
            self.operationInProgress = true;
            self.emitEvent('cart:operation:start', {
              url: requestUrl,
              method: requestMethod,
              type: 'xhr'
            });
          });

          xhr.addEventListener('load', function() {
            try {
              const data = JSON.parse(xhr.responseText);
              self.log('XHR cart WRITE operation complete:', requestUrl, data);
              self.emitEvent('cart:operation:complete', {
                url: requestUrl,
                data,
                type: 'xhr'
              });
            } catch (e) {
              self.log('XHR cart WRITE operation complete (non-JSON):', requestUrl);
              self.emitEvent('cart:operation:complete', {
                url: requestUrl,
                data: null,
                type: 'xhr'
              });
            }
            self.operationInProgress = false;
          });

          xhr.addEventListener('error', function() {
            self.log('XHR cart operation error:', requestUrl);
            self.emitEvent('cart:operation:error', {
              url: requestUrl,
              error: 'XHR error',
              type: 'xhr'
            });
            self.operationInProgress = false;
          });

          xhr.addEventListener('abort', function() {
            self.log('XHR cart operation aborted:', requestUrl);
            self.emitEvent('cart:operation:error', {
              url: requestUrl,
              error: 'XHR aborted',
              type: 'xhr'
            });
            self.operationInProgress = false;
          });
        }

        return originalOpen.apply(this, arguments);
      };

      return xhr;
    };

    // Copy prototype
    window.XMLHttpRequest.prototype = OriginalXHR.prototype;

    this.log('XMLHttpRequest interceptor installed');
  }

  /**
   * Check if a URL is a cart operation
   */
  isCartOperation(url) {
    if (!url) return false;

    const cartEndpoints = [
      '/cart.js',
      '/cart/add',
      '/cart/add.js',
      '/cart/change',
      '/cart/change.js',
      '/cart/update',
      '/cart/update.js',
      '/cart/clear',
      '/cart/clear.js'
    ];

    return cartEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Check if a cart operation is a WRITE operation (modification)
   * Returns false for READ operations to prevent infinite loops
   */
  isCartWriteOperation(url, method) {
    if (!url) return false;

    // Normalize method
    const normalizedMethod = (method || 'GET').toUpperCase();

    // /cart.js with GET = READ operation (don't emit completion events)
    if (url.includes('/cart.js') && normalizedMethod === 'GET') {
      return false;
    }

    // Everything else = WRITE operation (emit completion events)
    const writeEndpoints = [
      '/cart/add',
      '/cart/change',
      '/cart/update',
      '/cart/clear'
    ];

    return writeEndpoints.some(endpoint => url.includes(endpoint)) ||
           (url.includes('/cart.js') && normalizedMethod === 'POST');
  }

  /**
   * Emit a custom event
   */
  emitEvent(eventName, detail) {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
      cancelable: false
    });

    document.dispatchEvent(event);
    this.log('Event emitted:', eventName, detail);
  }

  /**
   * Check if a cart operation is currently in progress
   */
  isOperationInProgress() {
    return this.operationInProgress;
  }

  /**
   * Wait for any current operation to complete
   */
  async waitForOperation(timeout = 5000) {
    if (!this.operationInProgress) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Cart operation timeout'));
      }, timeout);

      const handler = () => {
        clearTimeout(timeoutId);
        document.removeEventListener('cart:operation:complete', handler);
        document.removeEventListener('cart:operation:error', handler);
        resolve();
      };

      document.addEventListener('cart:operation:complete', handler);
      document.addEventListener('cart:operation:error', handler);
    });
  }
}

// Initialize globally
if (!window.cartOperationInterceptor) {
  window.cartOperationInterceptor = new CartOperationInterceptor();
  console.log('[CartInterceptor] Cart Operation Interceptor initialized successfully');
} else {
  console.warn('[CartInterceptor] Cart Operation Interceptor already initialized');
}
